// ADR-132: bulk import (initial seed from radar-ingestion's code maps, bulk edits).
// Always previews first (dry-run); applying is all-or-nothing on the server.
import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Chip,
  Typography,
  CircularProgress,
} from '@mui/material';
import { INGESTION_IMPORT_MODES } from '../../../constants/ingestion';
import { describeHistoryRow, parseImportPayload } from '../../../utils/ingestionMappings';
import { ReportSummary, ReportList, ReportSeparator } from './styles';

// Same wording as the History tab: “Title” — change.
const ReportRow = ({ entry }) => {
  const { title, change } = describeHistoryRow(entry);
  return (
    <li>
      “{title}”<ReportSeparator aria-hidden="true">—</ReportSeparator>{change}
    </li>
  );
};

const ImportDialog = ({ open, provider, providerLabel, onClose, onPreview, onApply }) => {
  const [text, setText] = useState('');
  const [mode, setMode] = useState(INGESTION_IMPORT_MODES.MERGE);
  const [report, setReport] = useState(null);
  const [parseError, setParseError] = useState(null);
  const [serverError, setServerError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [providerMismatch, setProviderMismatch] = useState(null);

  useEffect(() => {
    if (open) {
      setText('');
      setMode(INGESTION_IMPORT_MODES.MERGE);
      setReport(null);
      setParseError(null);
      setServerError(null);
      setProviderMismatch(null);
      setBusy(false);
    }
  }, [open]);

  const resetReport = () => {
    setReport(null);
    setServerError(null);
  };

  const handleFile = async (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }
    setText(await file.text());
    resetReport();
  };

  const parse = () => {
    try {
      const parsed = parseImportPayload(text);
      setParseError(null);
      setProviderMismatch(parsed.provider && parsed.provider !== provider ? parsed.provider : null);
      return parsed;
    } catch (error) {
      setParseError(error.message);
      return null;
    }
  };

  const handlePreview = async () => {
    const parsed = parse();
    if (!parsed) {
      return;
    }
    try {
      setBusy(true);
      setServerError(null);
      setReport(await onPreview({ items: parsed.items, mode }));
    } catch (error) {
      setServerError(error?.message || 'Preview failed');
    } finally {
      setBusy(false);
    }
  };

  const handleApply = async () => {
    const parsed = parse();
    if (!parsed) {
      return;
    }
    try {
      setBusy(true);
      await onApply({ items: parsed.items, mode });
    } catch (error) {
      setServerError(error?.message || 'Import failed');
      setBusy(false);
    }
  };

  const changes = report ? report.summary.create + report.summary.update + report.summary.delete : 0;
  const canApply = report && report.summary.errors === 0 && changes > 0 && !providerMismatch;

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>Import mappings · {providerLabel}</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="textSecondary" paragraph>
          Upload a JSON export.
          Items look like {'{ "sourcePattern": "Recruiter", "positionLabel": "" }'}: an empty position label means
          &nbsp;&quot;no position&quot;. If an item has no &quot;notes&quot; property, the mapping keeps its current
          notes; set {'"notes": ""'} to clear them. Every import is previewed first and applied in one step.
        </Typography>

        <Button variant="outlined" component="label" size="small" sx={{ mb: 2 }}>
          Choose file
          <input type="file" accept="application/json,.json" hidden onChange={handleFile} />
        </Button>

        <TextField
          label="…or paste JSON"
          value={text}
          onChange={(e) => { setText(e.target.value); resetReport(); }}
          fullWidth
          multiline
          minRows={4}
          maxRows={10}
          InputProps={{ sx: { fontFamily: 'monospace', fontSize: 12 } }}
        />

        <FormControl size="small" sx={{ mt: 2, minWidth: 320 }}>
          <InputLabel>Mode</InputLabel>
          <Select value={mode} label="Mode" onChange={(e) => { setMode(e.target.value); resetReport(); }}>
            <MenuItem value={INGESTION_IMPORT_MODES.MERGE}>Merge: add and update, keep everything else</MenuItem>
            <MenuItem value={INGESTION_IMPORT_MODES.REPLACE}>Replace: also remove mappings not in the file</MenuItem>
          </Select>
        </FormControl>

        {parseError && <Alert severity="error" sx={{ mt: 2 }}>{parseError}</Alert>}
        {providerMismatch && (
          <Alert severity="error" sx={{ mt: 2 }}>
            This file is for {providerMismatch}, but you are importing into {providerLabel}. Switch tabs or edit the file.
          </Alert>
        )}
        {serverError && <Alert severity="error" sx={{ mt: 2 }}>{serverError}</Alert>}

        {report && (
          <>
            <ReportSummary>
              <Chip size="small" color="success" label={`${report.summary.create} to add`} />
              <Chip size="small" color="info" label={`${report.summary.update} to change`} />
              <Chip size="small" color={report.summary.delete > 0 ? 'warning' : 'default'} label={`${report.summary.delete} to remove`} />
              <Chip size="small" label={`${report.summary.unchanged} unchanged`} />
              <Chip size="small" color={report.summary.errors > 0 ? 'error' : 'default'} label={`${report.summary.errors} errors`} />
            </ReportSummary>

            {report.errors.length > 0 && (
              <>
                <Typography variant="subtitle2" color="error">Fix these before importing</Typography>
                <ReportList>
                  {report.errors.map((error) => (
                    <li key={`${error.index}-${error.message}`}>
                      Item {error.index + 1}{error.sourcePattern ? ` (“${error.sourcePattern}”)` : ''}: {error.message}
                    </li>
                  ))}
                </ReportList>
              </>
            )}
            {report.warnings.length > 0 && (
              <>
                <Typography variant="subtitle2">Warnings</Typography>
                <ReportList>
                  {report.warnings.map((warning) => (
                    <li key={`${warning.index}-${warning.warning}`}>“{warning.sourcePattern}”: {warning.warning}</li>
                  ))}
                </ReportList>
              </>
            )}
            {report.creates.length > 0 && (
              <>
                <Typography variant="subtitle2">To add</Typography>
                <ReportList>
                  {report.creates.map((item) => (
                    <ReportRow key={`c-${item.sourcePattern}`} entry={{ after: item }} />
                  ))}
                </ReportList>
              </>
            )}
            {report.updates.length > 0 && (
              <>
                <Typography variant="subtitle2">To change</Typography>
                <ReportList>
                  {report.updates.map((item) => (
                    <ReportRow key={`u-${item.id}`} entry={item} />
                  ))}
                </ReportList>
              </>
            )}
            {report.deletes.length > 0 && (
              <>
                <Typography variant="subtitle2">To remove</Typography>
                <ReportList>
                  {report.deletes.map((item) => (
                    <ReportRow key={`d-${item.id}`} entry={{ before: item }} />
                  ))}
                </ReportList>
              </>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button onClick={handlePreview} disabled={busy || !text.trim()}>
          {busy && !report ? <CircularProgress size={18} /> : 'Preview'}
        </Button>
        <Button variant="contained" onClick={handleApply} disabled={busy || !canApply}>
          {busy && report ? <CircularProgress size={18} /> : `Apply ${changes || ''} change${changes === 1 ? '' : 's'}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ImportDialog;
